/**
 * Action Executor for AI Agent
 * Coordinates execution of all action types
 */
import { NavigateFunction } from 'react-router-dom';
import { NavigationActions } from './NavigationActions';
import { DashboardActions } from './DashboardActions';
import { ExportActions } from './ExportActions';

export interface Action {
  type: string;
  params: Record<string, any>;
  description: string;
}

export interface ActionPlan {
  actions: Action[];
  needs_confirmation: boolean;
  intent: any;
}

export class ActionExecutor {
  private navigationActions: NavigationActions;
  private dashboardActions: DashboardActions;
  private exportActions: ExportActions;

  constructor(
    navigate: NavigateFunction,
    dashboardService: any,
    exportService: any
  ) {
    this.navigationActions = new NavigationActions(navigate);
    this.dashboardActions = new DashboardActions(dashboardService);
    this.exportActions = new ExportActions(exportService);
  }

  /**
   * Execute a single action
   */
  async executeAction(action: Action): Promise<{
    success: boolean;
    error?: string;
    result?: any;
    undoable?: boolean;
  }> {
    switch (action.type) {
      case 'navigate':
        return this.navigationActions.execute(action as any);

      case 'create_dashboard':
        return this.dashboardActions.createDashboard(action as any);

      case 'modify_dashboard':
        return this.dashboardActions.modifyDashboard(action as any);

      case 'export_data':
        return this.exportActions.execute(action as any);

      case 'query_analytics':
        // This returns data, not performs action
        return { success: true, result: 'Query result' };

      // Calendar integration actions
      case 'disconnect_calendar':
        return this.handleCalendarDisconnect(action);

      case 'trigger_calendar_sync':
        return this.handleCalendarSync(action);

      // Meeting scheduler actions
      case 'open_meeting_scheduler':
        // Navigate to calendar with meeting scheduler action
        return this.navigationActions.execute({
          type: 'navigate',
          params: { to: action.params.navigate_to || '/calendar?action=schedule-meeting' },
          description: 'Opening meeting scheduler'
        } as any);

      // Room booking actions
      case 'open_room_booking':
        return this.navigationActions.execute({
          type: 'navigate',
          params: { to: action.params.navigate_to || '/calendar?tab=rooms' },
          description: 'Opening room booking'
        } as any);

      default:
        // For unknown action types, check if there's a navigate_to parameter
        if (action.params?.navigate_to || action.params?.to) {
          return this.navigationActions.execute({
            type: 'navigate',
            params: { to: action.params.navigate_to || action.params.to },
            description: action.description || 'Navigating'
          } as any);
        }
        return {
          success: false,
          error: `Unknown action type: ${action.type}`
        };
    }
  }

  /**
   * Handle calendar disconnection
   */
  private async handleCalendarDisconnect(action: Action): Promise<{
    success: boolean;
    error?: string;
    result?: any;
  }> {
    try {
      const { CalendarIntegrationService } = await import('../calendarIntegrationService');
      const churchId = localStorage.getItem('selectedChurchId');
      if (!churchId) {
        return { success: false, error: 'No church selected' };
      }

      const service = new CalendarIntegrationService(churchId);
      await service.disconnect(action.params.integration_id);

      return { success: true, result: { disconnected: true } };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to disconnect calendar' };
    }
  }

  /**
   * Handle calendar sync trigger
   */
  private async handleCalendarSync(action: Action): Promise<{
    success: boolean;
    error?: string;
    result?: any;
  }> {
    try {
      const { CalendarIntegrationService } = await import('../calendarIntegrationService');
      const churchId = localStorage.getItem('selectedChurchId');
      if (!churchId) {
        return { success: false, error: 'No church selected' };
      }

      const service = new CalendarIntegrationService(churchId);
      const result = await service.triggerSync(action.params.integration_id);

      return { success: true, result };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to sync calendar' };
    }
  }

  /**
   * Execute full action plan
   */
  async executeActionPlan(
    actionPlan: ActionPlan,
    onProgress?: (index: number, total: number) => void
  ): Promise<{
    success: boolean;
    results: any[];
    errors: string[];
  }> {
    const results: any[] = [];
    const errors: string[] = [];
    
    for (let i = 0; i < actionPlan.actions.length; i++) {
      const action = actionPlan.actions[i];
      
      if (onProgress) {
        onProgress(i + 1, actionPlan.actions.length);
      }
      
      const result = await this.executeAction(action);
      results.push(result);
      
      if (!result.success) {
        errors.push(result.error || 'Unknown error');
        // Stop on first error
        break;
      }
    }
    
    return {
      success: errors.length === 0,
      results,
      errors
    };
  }

  /**
   * Check if action plan can be executed
   */
  canExecute(
    actionPlan: ActionPlan | undefined,
    userPermissions: string[]
  ): {
    canExecute: boolean;
    missingPermissions: string[];
  } {
    const missingPermissions: string[] = [];

    // Handle null/undefined action plan or empty actions
    if (!actionPlan?.actions || actionPlan.actions.length === 0) {
      return { canExecute: true, missingPermissions: [] };
    }

    for (const action of actionPlan.actions) {
      if (action.type === 'navigate') {
        // Support both 'page' and 'to' params for navigation
        const page = action.params?.page || action.params?.to || action.params?.navigate_to;
        if (page && !NavigationActions.canNavigateTo(page, userPermissions)) {
          missingPermissions.push(`Navigate to ${page}`);
        }
      } else if (action.type === 'create_dashboard') {
        if (!userPermissions.includes('dashboards.create')) {
          missingPermissions.push('Create dashboards');
        }
      } else if (action.type === 'modify_dashboard') {
        if (!userPermissions.includes('dashboards.edit')) {
          missingPermissions.push('Edit dashboards');
        }
      } else if (action.type === 'export_data') {
        if (!userPermissions.includes('data.export')) {
          missingPermissions.push('Export data');
        }
      }
      // Other action types (calendar, room booking, etc.) don't require special permissions
    }

    return {
      canExecute: missingPermissions.length === 0,
      missingPermissions
    };
  }

  /**
   * Generate undo actions for action plan
   */
  generateUndoActions(
    actionPlan: ActionPlan,
    executionResults: any[]
  ): Action[] {
    const undoActions: Action[] = [];
    
    for (let i = 0; i < actionPlan.actions.length; i++) {
      const action = actionPlan.actions[i];
      const result = executionResults[i];
      
      if (!result?.success) continue;
      
      switch (action.type) {
        case 'create_dashboard':
          if (result.dashboardId) {
            undoActions.push({
              type: 'delete_dashboard',
              params: { dashboardId: result.dashboardId },
              description: `Delete dashboard "${action.params.title}"`
            });
          }
          break;
          
        case 'navigate':
          // Navigation can't be undone
          break;
          
        // Add other undo actions as needed
      }
    }
    
    return undoActions.reverse(); // Reverse order for undo
  }
}